<template>
    <div class="success-cases">
        <section class="page-header">
            <div class="container">
                <h1 class="page-title">成功案例</h1>
                <div class="breadcrumb">
                    <el-breadcrumb separator="/">
                        <el-breadcrumb-item :to="{ path: '/' }">首页</el-breadcrumb-item>
                        <el-breadcrumb-item>成功案例</el-breadcrumb-item>
                    </el-breadcrumb>
                </div>
            </div>
        </section>

        <section class="cases-list">
            <div class="container">
                <div class="cases-categories">
                    <el-radio-group v-model="currentCategory" @change="handleCategoryChange">
                        <el-radio-button v-for="cat in SUCCESS_CASE_CATEGORY_OPTIONS" :key="cat.value"
                            :value="cat.value">
                            {{ cat.label }}
                        </el-radio-button>
                    </el-radio-group>
                </div>

                <div class="cases-search">
                    <el-input v-model="searchKeyword" placeholder="搜索成功案例..." class="search-input">
                        <template #prefix>
                            <el-icon>
                                <Search />
                            </el-icon>
                        </template>
                    </el-input>
                </div>

                <div class="cases-grid">
                    <div class="cases-card" v-for="item in paginatedCases" :key="item.id" @click="goToDetail(item.id)"
                        data-aos="fade-up" data-aos-duration="800">
                        <div class="cases-image">
                            <el-image :src="item.image" fit="cover">
                                <template #placeholder>
                                    <div class="image-placeholder">
                                        <el-icon>
                                            <Picture />
                                        </el-icon>
                                    </div>
                                </template>
                            </el-image>
                            <div class="cases-category" :class="item.category">
                                {{ getSuccessCaseCategoryLabel(item.category) }}
                            </div>
                        </div>
                        <div class="cases-body">
                            <div class="cases-meta">
                                <span class="date">
                                    <el-icon>
                                        <Calendar />
                                    </el-icon>
                                    {{ formatDate(item.date) }}
                                </span>
                                <span class="views">
                                    <el-icon>
                                        <View />
                                    </el-icon>
                                    {{ item.views }}
                                </span>
                            </div>
                            <h3 class="cases-title">{{ item.title }}</h3>
                            <p class="cases-description">{{ item.description }}</p>
                            <el-button text type="primary" class="read-more">
                                查看案例
                                <el-icon class="el-icon--right">
                                    <ArrowRight />
                                </el-icon>
                            </el-button>
                        </div>
                    </div>
                </div>

                <div v-if="filteredTotal > pageSize" class="pagination">
                    <el-pagination v-model:current-page="currentPage" v-model:page-size="pageSize" :total="filteredTotal"
                        :page-sizes="[9, 18]" layout="总计, 每页, 上一页, 页码, 下一页, 跳转"
                        @size-change="handleSizeChange" @current-change="handleCurrentChange" />
                </div>
            </div>
        </section>
    </div>
</template>

<script setup lang="ts">
import { ref, computed, onMounted } from 'vue'
import { useRouter } from 'vue-router'
import { Calendar, Picture, View, Search, ArrowRight } from '@element-plus/icons-vue'
import AOS from 'aos'
import 'aos/dist/aos.css'
import {
    successCases,
    SUCCESS_CASE_CATEGORY_OPTIONS,
    SUCCESS_CASES_LIST_PATH,
    getSuccessCaseCategoryLabel,
} from '@/data/successCases'
import type { SuccessCaseCategory } from '@/data/successCases'

const router = useRouter()

const currentCategory = ref<'all' | SuccessCaseCategory>('all')
const searchKeyword = ref('')
const currentPage = ref(1)
const pageSize = ref(9)

const filteredList = computed(() => {
    let list = [...successCases]
    if (currentCategory.value !== 'all') {
        list = list.filter((c) => c.category === currentCategory.value)
    }
    if (searchKeyword.value.trim()) {
        const kw = searchKeyword.value.toLowerCase()
        list = list.filter(
            (c) =>
                c.title.toLowerCase().includes(kw) ||
                c.description.toLowerCase().includes(kw)
        )
    }
    return list
})

const filteredTotal = computed(() => filteredList.value.length)

const paginatedCases = computed(() => {
    const start = (currentPage.value - 1) * pageSize.value
    return filteredList.value.slice(start, start + pageSize.value)
})

const handleCategoryChange = () => {
    currentPage.value = 1
}

const handleSizeChange = (size: number) => {
    pageSize.value = size
    currentPage.value = 1
}

const handleCurrentChange = (page: number) => {
    currentPage.value = page
}

const formatDate = (date: string) => {
    return new Date(date).toLocaleDateString('zh-CN', {
        year: 'numeric',
        month: 'long',
        day: 'numeric'
    })
}

const goToDetail = (id: number) => {
    router.push(`${SUCCESS_CASES_LIST_PATH}/${id}`)
}

onMounted(() => {
    AOS.init({ duration: 1000, once: true })
})
</script>

<style scoped lang="scss">
.success-cases {
    padding-top: 80px;

    .page-header {
        background: linear-gradient(135deg, #1890ff 0%, #604FDF 100%);
        padding: 60px 0;
        color: #fff;
        margin-bottom: 60px;

        .page-title {
            font-size: 36px;
            margin-bottom: 10px;
        }

        .breadcrumb {
            :deep(.el-breadcrumb) {
                color: rgba(255, 255, 255, 0.8);

                .el-breadcrumb__item {
                    .el-breadcrumb__inner {
                        color: rgba(255, 255, 255, 0.8);

                        &:hover {
                            color: #fff;
                        }
                    }

                    &:last-child .el-breadcrumb__inner {
                        color: #fff;
                    }
                }
            }
        }
    }

    .cases-list {
        padding: 0 0 80px;

        .cases-categories {
            text-align: center;
            margin-bottom: 40px;
        }

        .cases-search {
            max-width: 400px;
            margin: 0 auto 40px;

            .search-input {
                .el-input__wrapper {
                    box-shadow: 0 2px 12px rgba(0, 0, 0, 0.04);
                }
            }
        }

        .cases-grid {
            display: grid;
            grid-template-columns: repeat(3, 1fr);
            gap: 30px;
            margin-bottom: 60px;

            .cases-card {
                background: #fff;
                border-radius: 12px;
                overflow: hidden;
                box-shadow: 0 4px 12px rgba(0, 0, 0, 0.08);
                transition: transform 0.3s;
                cursor: pointer;

                &:hover {
                    transform: translateY(-10px);
                }

                .cases-image {
                    position: relative;
                    height: 240px;

                    .el-image {
                        width: 100%;
                        height: 100%;
                    }

                    .image-placeholder {
                        display: flex;
                        align-items: center;
                        justify-content: center;
                        height: 100%;
                        background: #f5f5f5;
                        color: #999;
                    }

                    .cases-category {
                        position: absolute;
                        top: 20px;
                        right: 20px;
                        padding: 6px 12px;
                        border-radius: 20px;
                        color: #fff;
                        font-size: 14px;

                        &.logistics {
                            background: #1890ff;
                        }

                        &.ecommerce {
                            background: #eb2f96;
                        }

                        &.manufacturing {
                            background: #597ef7;
                        }

                        &.government {
                            background: #52c41a;
                        }

                        &.retail {
                            background: #fa8c16;
                        }

                        &.hospitality {
                            background: #13c2c2;
                        }

                        &.erp {
                            background: #722ed1;
                        }

                        &.sports {
                            background: #237804;
                        }

                        &.healthcare {
                            background: #f5222d;
                        }
                    }
                }

                .cases-body {
                    padding: 24px;

                    .cases-meta {
                        display: flex;
                        align-items: center;
                        margin-bottom: 16px;
                        color: #999;
                        font-size: 14px;

                        .date,
                        .views {
                            display: flex;
                            align-items: center;

                            .el-icon {
                                margin-right: 4px;
                            }
                        }

                        .date {
                            margin-right: 20px;
                        }
                    }

                    .cases-title {
                        font-size: 20px;
                        color: #333;
                        margin-bottom: 12px;
                        line-height: 1.4;
                        display: -webkit-box;
                        -webkit-line-clamp: 2;
                        -webkit-box-orient: vertical;
                        overflow: hidden;
                    }

                    .cases-description {
                        font-size: 14px;
                        color: #666;
                        line-height: 1.6;
                        margin-bottom: 20px;
                        display: -webkit-box;
                        -webkit-line-clamp: 3;
                        -webkit-box-orient: vertical;
                        overflow: hidden;
                    }

                    .read-more {
                        padding: 0;
                        height: auto;

                        .el-icon {
                            transition: transform 0.3s;
                        }

                        &:hover .el-icon {
                            transform: translateX(4px);
                        }
                    }
                }
            }
        }

        .pagination {
            display: flex;
            justify-content: center;
        }
    }
}

@media (max-width: 1200px) {
    .success-cases .cases-list .cases-grid {
        grid-template-columns: repeat(2, 1fr);
    }
}

@media (max-width: 768px) {
    .success-cases {
        .page-header {
            padding: 40px 0;

            .page-title {
                font-size: 28px;
            }
        }

        .cases-list {
            .cases-grid {
                grid-template-columns: 1fr;
            }

            .cases-search {
                max-width: 100%;
            }

            .cases-categories :deep(.el-radio-group) {
                display: flex;
                flex-wrap: wrap;
                justify-content: center;
                gap: 8px;
            }
        }
    }
}
</style>
